'use client'

import {useEffect} from 'react'
import {suppressNextCompatibleClick} from '../../lib/pointer-events'

const TAP_MOVE_TOLERANCE_PX = 8
const ACTIVATABLE_SELECTOR = [
    'button',
    'a[href]',
    'label',
    'input',
    'select',
    'textarea',
    '[role="button"]',
    '[role="checkbox"]',
    '[role="menuitem"]',
    '[role="option"]',
    '[role="radio"]',
    '[role="switch"]',
    '[data-pen-activatable="true"]',
].join(',')

type PenTap = {
    pointerId: number
    target: EventTarget | null
    clientX: number
    clientY: number
    canceled: boolean
}

const textInputTypes = new Set([
    '',
    'email',
    'number',
    'password',
    'search',
    'tel',
    'text',
    'url',
])

function isPrimaryPenEvent(event: PointerEvent) {
    return event.pointerType === 'pen' && event.isPrimary !== false
}

function isElement(target: EventTarget | null): target is Element {
    return target instanceof Element
}

function isDisabledByFieldset(element: HTMLElement) {
    let current: Element | null = element

    while (current) {
        const fieldset: Element | null = current.closest('fieldset[disabled]')
        if (!(fieldset instanceof HTMLFieldSetElement)) return false

        const firstLegend = Array.from(fieldset.children).find(
            (child): child is HTMLLegendElement => child instanceof HTMLLegendElement
        )

        if (!firstLegend?.contains(element)) return true
        current = fieldset.parentElement
    }

    return false
}

function isDisabledElement(element: HTMLElement) {
    if (element.getAttribute('aria-disabled') === 'true') return true
    if (element instanceof HTMLLabelElement && element.control instanceof HTMLElement) {
        return isDisabledElement(element.control)
    }
    if (isDisabledByFieldset(element)) return true
    if (element instanceof HTMLButtonElement ||
        element instanceof HTMLInputElement ||
        element instanceof HTMLSelectElement ||
        element instanceof HTMLTextAreaElement) {
        return element.disabled
    }
    return false
}

function isTextEntryElement(element: HTMLElement) {
    if (element instanceof HTMLTextAreaElement) return true
    if (!(element instanceof HTMLInputElement)) return false
    return textInputTypes.has(element.type)
}

function isToggleInput(element: HTMLElement): element is HTMLInputElement {
    return element instanceof HTMLInputElement &&
        (element.type === 'checkbox' || element.type === 'radio')
}

function focusElement(element: HTMLElement) {
    try {
        element.focus({preventScroll: true})
    } catch {
        element.focus()
    }
}

function getActivatableElement(target: EventTarget | null) {
    const candidate = isElement(target)
        ? target.closest(ACTIVATABLE_SELECTOR)
        : null
    return candidate instanceof HTMLElement ? candidate : null
}

function findActivatableElement(
    startTarget: EventTarget | null,
    endTarget: EventTarget | null,
    pointTarget: Element | null
) {
    const startCandidate = getActivatableElement(startTarget)
    const endCandidate = getActivatableElement(endTarget)
    const pointCandidate = getActivatableElement(pointTarget)
    const candidate = startCandidate && endCandidate?.contains(startCandidate)
        ? startCandidate
        : pointCandidate || startCandidate || endCandidate
    if (!candidate) return null

    if (startCandidate &&
        candidate !== startCandidate &&
        !candidate.contains(startCandidate) &&
        !startCandidate.contains(candidate)) {
        return null
    }

    return candidate
}

function hasMovedPastTapTolerance(tap: Pick<PenTap, 'clientX' | 'clientY'>, event: PointerEvent) {
    return Math.hypot(event.clientX - tap.clientX, event.clientY - tap.clientY) > TAP_MOVE_TOLERANCE_PX
}

function showPickerIfAvailable(target: HTMLElement) {
    const targetWithPicker = target as HTMLElement & {showPicker?: () => void}
    if (typeof targetWithPicker.showPicker !== 'function') return false

    try {
        targetWithPicker.showPicker()
        return true
    } catch {
        return false
    }
}

function setInputChecked(input: HTMLInputElement, checked: boolean) {
    const descriptor = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'checked')
    descriptor?.set?.call(input, checked)
}

type ReactInputChangeProps = {
    onChange?: (event: {
        target: HTMLInputElement
        currentTarget: HTMLInputElement
        type: 'change'
        preventDefault: () => void
        stopPropagation: () => void
        isDefaultPrevented: () => boolean
        isPropagationStopped: () => boolean
        persist: () => void
    }) => void
}

type ReactValueTracker = {
    getValue: () => string
    setValue: (value: string) => void
}

function getReactInputProps(input: HTMLInputElement) {
    const reactPropsKey = Object.keys(input).find((key) => key.startsWith('__reactProps$'))
    if (!reactPropsKey) return null
    return (input as unknown as Record<string, ReactInputChangeProps | undefined>)[reactPropsKey] ?? null
}

function getReactValueTracker(input: HTMLInputElement) {
    return (input as HTMLInputElement & {_valueTracker?: ReactValueTracker})._valueTracker ?? null
}

function notifyReactToggleChangeIfNeeded(input: HTMLInputElement) {
    const tracker = getReactValueTracker(input)
    const nextValue = String(input.checked)
    if (!tracker || tracker.getValue() === nextValue) return

    const props = getReactInputProps(input)
    if (typeof props?.onChange !== 'function') return

    let defaultPrevented = false
    let propagationStopped = false
    props.onChange({
        target: input,
        currentTarget: input,
        type: 'change',
        preventDefault: () => {
            defaultPrevented = true
        },
        stopPropagation: () => {
            propagationStopped = true
        },
        isDefaultPrevented: () => defaultPrevented,
        isPropagationStopped: () => propagationStopped,
        persist: () => {},
    })
    tracker.setValue(nextValue)
}

function activateToggleInput(input: HTMLInputElement, event: PointerEvent) {
    if (isDisabledElement(input)) return

    focusElement(input)
    event.preventDefault()
    if (input.readOnly || (input.type === 'radio' && input.checked)) {
        suppressNextCompatibleClick(event)
        return
    }

    setInputChecked(input, input.type === 'checkbox' ? !input.checked : true)
    input.dispatchEvent(new Event('input', {bubbles: true}))
    input.dispatchEvent(new Event('change', {bubbles: true}))
    notifyReactToggleChangeIfNeeded(input)
    suppressNextCompatibleClick(event)
}

function activatePenTarget(target: HTMLElement, event: PointerEvent) {
    if (target instanceof HTMLLabelElement && target.control instanceof HTMLElement) {
        const control = target.control
        focusElement(control)
        if (isToggleInput(control)) {
            activateToggleInput(control, event)
            return
        }
        if (isTextEntryElement(control)) return
        if (control instanceof HTMLSelectElement) {
            if (showPickerIfAvailable(control)) {
                suppressNextCompatibleClick(event)
                return
            }
        }
        if (showPickerIfAvailable(control)) {
            suppressNextCompatibleClick(event)
            return
        }

        event.preventDefault()
        control.click()
        suppressNextCompatibleClick(event)
        return
    }

    if (isTextEntryElement(target)) {
        focusElement(target)
        return
    }

    if (isToggleInput(target)) {
        activateToggleInput(target, event)
        return
    }

    if (target instanceof HTMLSelectElement) {
        focusElement(target)
        if (showPickerIfAvailable(target)) {
            suppressNextCompatibleClick(event)
            return
        }
    }

    if (target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement) {
        focusElement(target)
        if (showPickerIfAvailable(target)) {
            suppressNextCompatibleClick(event)
            return
        }
    }

    event.preventDefault()
    target.click()
    suppressNextCompatibleClick(event)
}

export function PenInteractionFallback() {
    useEffect(() => {
        let penTap: PenTap | null = null

        const handlePointerDown = (event: PointerEvent) => {
            if (!isPrimaryPenEvent(event) || event.button !== 0 || event.defaultPrevented) {
                penTap = null
                return
            }

            penTap = {
                pointerId: event.pointerId,
                target: event.target,
                clientX: event.clientX,
                clientY: event.clientY,
                canceled: false,
            }

            const tap = penTap
            queueMicrotask(() => {
                if (penTap === tap && event.defaultPrevented) {
                    penTap = null
                }
            })
        }

        const handlePointerMove = (event: PointerEvent) => {
            if (!penTap ||
                !isPrimaryPenEvent(event) ||
                penTap.pointerId !== event.pointerId) {
                return
            }

            const tap = penTap
            queueMicrotask(() => {
                if (penTap === tap && event.defaultPrevented) {
                    penTap.canceled = true
                }
            })

            if (hasMovedPastTapTolerance(penTap, event)) {
                penTap.canceled = true
            }
        }

        const handlePointerUp = (event: PointerEvent) => {
            const tap = penTap
            penTap = null

            if (!tap ||
                event.defaultPrevented ||
                tap.canceled ||
                !isPrimaryPenEvent(event) ||
                tap.pointerId !== event.pointerId ||
                hasMovedPastTapTolerance(tap, event)) {
                return
            }

            const pointTarget = typeof document.elementFromPoint === 'function'
                ? document.elementFromPoint(event.clientX, event.clientY)
                : null
            const target = findActivatableElement(tap.target, event.target, pointTarget)
            if (!target) return
            if (isDisabledElement(target)) {
                suppressNextCompatibleClick(event)
                return
            }

            queueMicrotask(() => {
                if (event.defaultPrevented || !target.isConnected) return
                activatePenTarget(target, event)
            })
        }

        const clearPenTap = () => {
            penTap = null
        }

        document.addEventListener('pointerdown', handlePointerDown, true)
        document.addEventListener('pointermove', handlePointerMove, true)
        document.addEventListener('pointerup', handlePointerUp, true)
        document.addEventListener('pointercancel', clearPenTap, true)
        document.addEventListener('lostpointercapture', clearPenTap, true)
        document.addEventListener('scroll', clearPenTap, true)

        return () => {
            document.removeEventListener('pointerdown', handlePointerDown, true)
            document.removeEventListener('pointermove', handlePointerMove, true)
            document.removeEventListener('pointerup', handlePointerUp, true)
            document.removeEventListener('pointercancel', clearPenTap, true)
            document.removeEventListener('lostpointercapture', clearPenTap, true)
            document.removeEventListener('scroll', clearPenTap, true)
        }
    }, [])

    return null
}
